import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  Landmark,
  Loader2,
  Plus,
  Settings2,
  UploadCloud,
  X,
} from "lucide-react";

import { Button } from "../../../components/ui/button";
import { Checkbox } from "../../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { API } from "../../../constant/api";
import { useBankName } from "../../../hooks/use-bank-name";
import { useBusinessImageUpload } from "../../../hooks/use-business-image-upload";
import useAuthAxios from "../../../utils/baseAxios";
import { errorMessage } from "../../../utils/errorMessage";
import AddBanks from "../../banks/AddBanks";
import AdvancedSettingsDialog from "./AdvancedSettingsDialog";
import type { PaymentPageRow } from "./paymentColumns";

type Bank = {
  _id: string;
  accountName?: string;
  accountNumber?: string;
  bankCode?: string;
  isDefault?: boolean;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  businessId: string;
  /** The merchant's saved payout banks — one gets tied to the page. */
  banks: Bank[];
  onCreated: (reference: string) => void;
  /**
   * When set, the dialog runs in edit mode: it prefills from this page and
   * PUTs the changes instead of creating a new page.
   */
  page?: PaymentPageRow | null;
  onUpdated?: () => void;
};

const CreatePaymentDialog = ({
  open,
  onOpenChange,
  businessId,
  banks,
  onCreated,
  page,
  onUpdated,
}: Props) => {
  const queryClient = useQueryClient();
  const axiosInstance = useAuthAxios();
  const isEdit = !!page;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [displayAmount, setDisplayAmount] = useState("");
  const [fixedAmount, setFixedAmount] = useState(false);
  const [collectData, setCollectData] = useState(false);
  const [redirectLink, setRedirectLink] = useState("");
  const [notificationEmail, setNotificationEmail] = useState("");
  const [bankId, setBankId] = useState("");
  const [openAdvanced, setOpenAdvanced] = useState(false);
  const [openAddBank, setOpenAddBank] = useState(false);
  // A bank just added inline — matched and selected once the refreshed list
  // (via the shared business query) flows back in through props.
  const [pendingBank, setPendingBank] = useState<{
    accountNumber: string;
    bankCode: string;
  } | null>(null);

  // Same signed-URL handshake the business logo uses, just a different type.
  const seoImage = useBusinessImageUpload({ documentType: "PAGE" });

  // Saved accounts store only a bank code; the picker shows the bank itself.
  const { bankName } = useBankName();

  const reset = () => {
    setName("");
    setDescription("");
    setAmount("");
    setDisplayAmount("");
    setFixedAmount(false);
    setCollectData(false);
    setRedirectLink("");
    setNotificationEmail("");
    setBankId("");
    setPendingBank(null);
    seoImage.clear();
  };

  // Prefill whenever the dialog opens. Create mode defaults to the default
  // payout bank; edit mode restores the page's own values and its tied bank.
  useEffect(() => {
    if (!open) return;

    const defaultBank = banks?.find((b) => b.isDefault) ?? banks?.[0];

    if (page) {
      setName(page.name ?? "");
      setDescription(page.description ?? "");
      const hasAmount = !!page.amount;
      setFixedAmount(hasAmount);
      setAmount(hasAmount ? String(page.amount) : "");
      setDisplayAmount(
        hasAmount ? Number(page.amount).toLocaleString("en-US") : ""
      );

      // Match the page's current bank by account; fall back to the default.
      const tied = banks?.find(
        (b) =>
          b.accountNumber === page.bank?.accountNumber &&
          b.bankCode === page.bank?.bankCode
      );
      setBankId((tied ?? defaultBank)?._id ?? "");
    } else {
      setBankId(defaultBank?._id ?? "");
    }
    // Prefill only on open / identity change — not on every banks reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, page?.reference]);

  // Once a freshly added bank shows up in the refreshed list, select it.
  useEffect(() => {
    if (!pendingBank) return;
    const added = banks?.find(
      (b) =>
        b.accountNumber === pendingBank.accountNumber &&
        b.bankCode === pendingBank.bankCode
    );
    if (added) {
      setBankId(added._id);
      setPendingBank(null);
    }
  }, [banks, pendingBank]);

  // Re-format the amount with thousands separators once typing pauses.
  useEffect(() => {
    const handler = setTimeout(() => {
      if (amount) setDisplayAmount(Number(amount).toLocaleString("en-US"));
    }, 500);
    return () => clearTimeout(handler);
  }, [amount]);

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/,/g, "");
    if (/^\d*\.?\d*$/.test(value)) {
      setAmount(value);
      setDisplayAmount(value);
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) seoImage.upload(file);
  };

  const savePage = useMutation({
    mutationFn: async (payload: Record<string, any>) => {
      if (isEdit) {
        const { data } = await axiosInstance.put(
          API.updatePaymentPage(businessId, page!.reference),
          payload
        );
        return data;
      }
      const { data } = await axiosInstance.post(
        API.createPaymentLink(businessId),
        payload
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ["payment-pages"] });
      onOpenChange(false);
      if (isEdit) {
        onUpdated?.();
      } else {
        onCreated(data?.data?.page?.reference);
      }
      reset();
    },
    onError: (e: any) => {
      toast.error(errorMessage(e?.response?.data?.message || e?.message));
    },
  });

  const selectedBank = banks?.find((b) => b._id === bankId);

  const canSubmit =
    name.trim() &&
    description.trim() &&
    (!fixedAmount || Number(amount) > 0) &&
    !!selectedBank;

  const bankPayload = selectedBank
    ? {
        bank: {
          accountNumber: selectedBank.accountNumber,
          bankCode: selectedBank.bankCode,
        },
      }
    : {};

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit || savePage.isPending) return;

    if (isEdit) {
      const nextAmount = fixedAmount && amount ? Number(amount) : null;
      // A page with no fixed amount sends no `amount` at all — the customer
      // names their own figure, and a 0 would be a value the page never had.
      // The one exception is clearing: if the page *did* carry an amount and
      // the merchant just unticked it, 0 has to go up or the partial PUT would
      // leave the old amount in place.
      const clearedAmount = nextAmount === null && !!page?.amount;

      // PUT is a partial update — send only the fields this form edits so
      // untouched settings (collect-data, advanced) are preserved.
      savePage.mutate({
        name: name.trim(),
        description: description.trim(),
        ...(nextAmount !== null && { amount: nextAmount }),
        ...(clearedAmount && { amount: 0 }),
        ...bankPayload,
      });
      return;
    }

    savePage.mutate({
      name: name.trim(),
      description: description.trim(),
      ...(seoImage.objectURL && { image: seoImage.objectURL }),
      ...(fixedAmount && amount && { amount: Number(amount) }),
      ...(collectData && { collectCustomerData: true }),
      ...(notificationEmail.trim() && {
        notificationEmail: notificationEmail.trim(),
      }),
      ...(redirectLink.trim() && { redirectLink: redirectLink.trim() }),
      ...bankPayload,
    });
  };

  // Account name • number • bank. The bank is dropped rather than shown blank
  // when the code isn't in either list, so the label never trails a separator.
  const bankLabel = (bank: Bank) =>
    [bank.accountName || "Account", bank.accountNumber, bankName(bank.bankCode)]
      .filter(Boolean)
      .join(" • ");

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="font-manrope mds:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="font-sora">
              {isEdit ? "Edit payment page" : "Create new payment page"}
            </DialogTitle>
            <DialogDescription>
              {isEdit
                ? "Update the name, amount or payout bank for this page."
                : "Share a link customers can pay through in crypto."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="page-name">Item name</Label>
              <Input
                id="page-name"
                placeholder="e.g. Aorthar Studios"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="page-description">Description</Label>
              <Input
                id="page-description"
                placeholder="Create a short description of the page"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="page-bank">Payout bank</Label>
                <button
                  type="button"
                  onClick={() => setOpenAddBank(true)}
                  className="flex items-center gap-1 text-[13px] font-semibold text-primary hover:underline"
                >
                  <Plus className="size-3.5" />
                  Add new bank
                </button>
              </div>
              <Select value={bankId} onValueChange={setBankId}>
                <SelectTrigger id="page-bank" className="h-[48px] rounded-xl">
                  <SelectValue placeholder="Select a payout bank" />
                </SelectTrigger>
                <SelectContent className="font-manrope">
                  {(banks ?? []).map((bank) => (
                    <SelectItem key={bank._id} value={bank._id}>
                      <span className="flex items-center gap-2">
                        <Landmark className="size-4 text-primary" />
                        {bankLabel(bank)}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[12px] font-medium text-muted-foreground">
                Settlements from this page pay into the selected account.
              </p>
            </div>

            <div className="space-y-3 rounded-xl border p-4">
              <label className="flex items-start gap-3">
                <Checkbox
                  checked={fixedAmount}
                  onCheckedChange={(checked) =>
                    setFixedAmount(checked === true)
                  }
                  className="mt-0.5"
                />
                <span className="text-[13px] font-medium text-foreground">
                  Allocate a fixed amount to this payment
                </span>
              </label>

              {fixedAmount && (
                <div className="space-y-2 pl-7">
                  <Label htmlFor="page-amount">Amount</Label>
                  <div className="flex items-center gap-1 rounded-xl border border-input bg-muted/50 px-4 focus-within:border-primary">
                    <span className="text-[15px] text-foreground">₦</span>
                    <input
                      id="page-amount"
                      inputMode="decimal"
                      placeholder="e.g. 50,000"
                      value={displayAmount}
                      onChange={handleAmountChange}
                      className="h-[48px] w-full bg-transparent text-sm outline-none"
                    />
                  </div>
                </div>
              )}

              {!isEdit && (
                <label className="flex items-start gap-3">
                  <Checkbox
                    checked={collectData}
                    onCheckedChange={(checked) =>
                      setCollectData(checked === true)
                    }
                    className="mt-0.5"
                  />
                  <span className="text-[13px] font-medium text-foreground">
                    Collect customer personal data (phone number and email)
                  </span>
                </label>
              )}
            </div>

            {!isEdit && (
              <div className="space-y-2">
                <Label>SEO image (optional)</Label>
                <p className="text-[12px] font-medium text-muted-foreground">
                  Shown to customers when the link is shared on social media.
                </p>

                {seoImage.file ? (
                  <div className="flex items-center justify-between gap-2 rounded-xl border p-2">
                    <span className="truncate rounded-lg bg-accent px-2 py-1 text-[12px] font-medium text-primary">
                      {seoImage.file.name}
                    </span>
                    {seoImage.isUploading ? (
                      <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
                    ) : (
                      <button
                        type="button"
                        onClick={seoImage.clear}
                        className="shrink-0 text-primary"
                        aria-label="Remove image"
                      >
                        <X className="size-4" />
                      </button>
                    )}
                  </div>
                ) : (
                  <label className="flex h-[45px] cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-input bg-muted/50 text-[14px] font-medium text-primary transition-colors hover:bg-muted">
                    <UploadCloud className="size-4" />
                    Choose a file to upload
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="sr-only"
                    />
                  </label>
                )}
              </div>
            )}

            {!isEdit && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setOpenAdvanced(true)}
                className="w-full gap-2 bg-accent text-primary hover:bg-accent/70"
              >
                <Settings2 className="size-4" />
                {redirectLink || notificationEmail
                  ? "Edit advanced settings"
                  : "Add advanced settings"}
              </Button>
            )}

            <DialogFooter>
              <Button
                type="submit"
                disabled={!canSubmit || savePage.isPending}
                className="h-[52px] w-full rounded-xl text-[15px] font-semibold"
              >
                {savePage.isPending ? (
                  <Loader2 className="size-5 animate-spin" />
                ) : isEdit ? (
                  "Save changes"
                ) : (
                  "Create page"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AdvancedSettingsDialog
        open={openAdvanced}
        onOpenChange={setOpenAdvanced}
        businessId={businessId}
        redirectLink={redirectLink}
        setRedirectLink={setRedirectLink}
        notificationEmail={notificationEmail}
        setNotificationEmail={setNotificationEmail}
      />

      {openAddBank && (
        <AddBanks
          nested
          businessID={businessId}
          setOpenAddBank={setOpenAddBank}
          onAdded={setPendingBank}
        />
      )}
    </>
  );
};

export default CreatePaymentDialog;
